import React, { useEffect, useState } from 'react';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { GoogleMapEmbed } from '../components/common/GoogleMapEmbed';
import { Icon } from '../lib/icons';
import { dataService } from '../services/dataService';
import { useLanguage } from '../context/LanguageContext';

function StylizedMap({ pins }) {
  return (
    <div className="relative h-72 w-full overflow-hidden rounded-card border border-paelbi-line bg-[#EDE7DD]">
      {/* sea */}
      <div className="absolute -right-10 bottom-0 h-2/3 w-2/3 rotate-6 rounded-[40px] bg-[#BFD8DA]/70" />
      <div className="absolute right-0 top-0 h-1/3 w-1/3 rounded-bl-[60px] bg-[#BFD8DA]/60" />
      {/* roads */}
      <div className="absolute left-1/4 top-0 h-full w-1 bg-white/70" />
      <div className="absolute left-0 top-1/2 h-1 w-full bg-white/70" />
      <div className="absolute left-2/3 top-0 h-full w-1 rotate-12 bg-white/60" />
      {pins.map((p) => (
        <span key={`${p.icon}-${p.top}-${p.left}`} style={{ top: p.top, left: p.left }} className="absolute flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-paelbi-brown shadow-soft">
          <Icon name={p.icon} size={16} />
        </span>
      ))}
    </div>
  );
}

export function VicinoATe() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState('all');
  const [query, setQuery] = useState('');
  const { config, t, lang } = useLanguage();
  const mapCfg = config?.tenant?.settings?.map;
  useEffect(() => { dataService.getNearby().then(setData); }, [lang]);
  if (!data) return null;

  const featured = data.featured.filter((f) => (active === 'all' || f.cat === active) && f.title.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="pb-20 pt-8">
      {/* HERO */}
      <Container>
        <section className="grid grid-cols-1 items-stretch overflow-hidden rounded-card border border-paelbi-line bg-paelbi-bg2/50 md:grid-cols-2">
          <div className="flex flex-col justify-center p-8 md:p-12">
            <h1 className="font-display text-4xl text-paelbi-ink md:text-[52px]">{data.hero.title}</h1>
            <span className="mt-4 block h-px w-12 bg-paelbi-brown/60" />
            <p className="mt-5 max-w-md font-sans text-base leading-relaxed text-paelbi-ink/75">{data.hero.text}</p>
          </div>
          <div className="relative min-h-[240px] md:min-h-0">
            <img src={data.hero.image} alt="Vicino a te" className="absolute inset-0 h-full w-full object-cover object-center" />
          </div>
        </section>
      </Container>

      {/* CATEGORY PILLS */}
      <Container className="mt-8">
        <div className="no-scrollbar -mx-6 flex gap-3 overflow-x-auto px-6 pb-2">
          {data.categories.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)}
              className={`flex h-11 shrink-0 items-center gap-2 rounded-btn border px-4 text-sm transition-colors ${active === c.id ? 'border-paelbi-brown bg-paelbi-brown text-white' : 'border-paelbi-line bg-white text-paelbi-ink/80 hover:border-paelbi-brown/50'}`}>
              {c.icon && <Icon name={c.icon} size={18} strokeWidth={1.75} />}{c.label}
            </button>
          ))}
        </div>
      </Container>

      {/* SEARCH + MAP */}
      <Container className="mt-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex h-12 flex-1 items-center gap-3 rounded-btn border border-paelbi-line bg-white px-4">
            <Icon name="search" size={18} className="text-paelbi-ink/50" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} type="text" placeholder="Cerca attività, luoghi, servizi..." aria-label="Cerca" className="w-full bg-transparent font-sans text-sm text-paelbi-ink outline-none placeholder:text-paelbi-ink/40" />
          </div>
          <Button variant="ghost" iconLeft="map" className="h-12 px-5 text-sm">Mappa</Button>
        </div>
      </Container>

      {/* FEATURED */}
      <Container className="mt-10">
        <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">In evidenza vicino a te</h2>
        <div className="mt-6">
          <GoogleMapEmbed
            map={mapCfg}
            title={t('Posizione della struttura')}
            height={288}
            fallback={<StylizedMap pins={data.mapPins} />}
          />
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((f) => (
            <article key={f.id} className="group flex flex-col overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card transition-all hover:-translate-y-1 hover:shadow-soft">
              <div className="relative">
                <img src={f.image} alt={f.title} loading="lazy" className="h-36 w-full object-cover" />
                <span className="absolute left-3 top-3 flex items-center gap-1 rounded-btn bg-white/95 px-2.5 py-1 text-xs text-paelbi-ink shadow-soft"><Icon name="signpost" size={13} className="text-paelbi-brown" />{f.distance}</span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <Badge className="w-fit">{f.badge}</Badge>
                <h3 className="mt-2 font-display text-base text-paelbi-ink">{f.title}</h3>
                <p className="mt-1 flex-1 font-sans text-sm text-paelbi-ink/70">{f.text}</p>
                <a href="#" className="mt-3 inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-paelbi-brown transition-all hover:gap-2.5">Scopri di più <Icon name="arrowRight" size={15} /></a>
              </div>
            </article>
          ))}
        </div>
      </Container>

      {/* OTHERS */}
      <Container className="mt-14">
        <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">Altri luoghi nei dintorni</h2>
        <div className="mt-6 overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card">
          {data.others.map((o, i) => (
            <a href="#" key={o.id} className={`flex items-center gap-4 p-4 transition-colors hover:bg-paelbi-bg ${i > 0 ? 'border-t border-paelbi-line' : ''}`}>
              <img src={o.image} alt={o.title} loading="lazy" className="h-16 w-16 shrink-0 rounded-[12px] object-cover" />
              <div className="flex-1">
                <h3 className="font-display text-base text-paelbi-ink">{o.title}</h3>
                <p className="mt-0.5 flex items-center gap-1.5 font-sans text-xs text-paelbi-ink/60"><Icon name="mapPin" size={13} />{o.sub}</p>
              </div>
              <span className="font-sans text-sm text-paelbi-ink/70">{o.distance}</span>
              <Icon name="chevronRight" size={18} className="text-paelbi-ink/50" />
            </a>
          ))}
        </div>
      </Container>

      {/* CTA */}
      <Container className="mt-14">
        <div className="flex flex-col items-start gap-4 rounded-card bg-paelbi-bg2/60 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white"><Icon name="mapPinned" size={22} className="text-paelbi-brown" /></span>
            <div>
              <h3 className="font-display text-xl text-paelbi-ink">Hai bisogno di consigli personalizzati?</h3>
              <p className="mt-1 font-sans text-sm text-paelbi-ink/70">Il nostro team è a disposizione per aiutarti a scoprire il meglio del territorio.</p>
            </div>
          </div>
          <Button variant="secondary" icon="arrowRight" className="shrink-0">Contattaci</Button>
        </div>
      </Container>
    </div>
  );
}

export default VicinoATe;
